/**
 * Live progress card for a deep email-research job (startEmailResearch).
 *
 * The pipeline runs in background (Cloud Tasks) and streams granular progress to
 * `users/{uid}/emailResearch/{jobId}`. This card narrates the current stage in
 * pt-BR with a determinate progress bar. It replaces the static tool chip that
 * never updated. When the job finishes (`complete`), the card disappears — the
 * final report arrives as a new message in the conversation. On `error`, it
 * shows a short pt-BR message without leaking internal details.
 */
import type { EmailResearchJobDoc, EmailResearchStage } from '@agente/shared'
import { Loader, MailSearch, TriangleAlert } from 'lucide-react'

import { useEmailResearchJob } from '@/hooks/useEmailResearchJob'

/**
 * Bloco de erro genérico em pt-BR, reusado tanto para a falha do job quanto para
 * uma falha na assinatura (onSnapshot). Nunca expõe a mensagem de erro crua.
 */
const errorCard = (
  <div className="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border px-2.5 py-2 text-xs">
    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
    <span className="font-medium">
      Não foi possível concluir a pesquisa nos e-mails. Tente novamente ou peça um novo relatório.
    </span>
  </div>
)

/** Rótulo pt-BR e avanço aproximado (0–100) por etapa do pipeline. */
function stageLabel(job: EmailResearchJobDoc): string {
  const { stage, batchesDone, batchesTotal } = job.progress
  switch (stage) {
    case 'fetching':
      return 'Buscando e-mails…'
    case 'triaging':
      return 'Triando e-mails…'
    case 'summarizing':
      return batchesTotal > 0
        ? `Resumindo e-mails (lote ${Math.min(batchesDone + 1, batchesTotal)} de ${batchesTotal})…`
        : 'Resumindo e-mails…'
    case 'rendering':
      return 'Gerando o relatório…'
    case 'sending':
      return 'Enviando o e-mail…'
    default:
      // 'queued' | 'done' — o job ainda não começou a rodar (aguardando o
      // disparo da tarefa) ou já finalizou (o card some antes de chegar aqui).
      return 'Preparando a pesquisa…'
  }
}

/** Avanço coarse por etapa, para a barra sempre progredir para a frente. */
function stageProgress(job: EmailResearchJobDoc): number {
  const { stage, batchesDone, batchesTotal } = job.progress
  const map: Record<EmailResearchStage, number> = {
    queued: 5,
    fetching: 20,
    triaging: 40,
    summarizing: batchesTotal > 0 ? 40 + Math.round((batchesDone / batchesTotal) * 40) : 55,
    rendering: 85,
    sending: 95,
    done: 100,
  }
  return map[stage] ?? 5
}

export function EmailResearchProgress({ jobId }: { jobId: string }) {
  const { job, error } = useEmailResearchJob(jobId)

  // Falha na assinatura (ex.: permission-denied): sem isto o card ficaria preso
  // no estado inicial com spinner para sempre. Mostramos o mesmo erro genérico.
  if (error != null) return errorCard

  // Doc ainda não existe (corrida tool→pipeline) ou ainda carregando: mostramos
  // um estado inicial neutro em vez de sumir/piscar.
  if (!job) {
    return (
      <div className="border-border/70 bg-muted/40 flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs">
        <Loader className="text-muted-foreground size-3.5 shrink-0 animate-spin" />
        <span className="text-foreground/90 font-medium">Preparando a pesquisa…</span>
      </div>
    )
  }

  // Concluído: o relatório chega como mensagem nova — o card de progresso some.
  if (job.status === 'complete') return null

  if (job.status === 'error') return errorCard

  const label = stageLabel(job)
  const progress = stageProgress(job)

  return (
    <div className="border-border/70 bg-muted/40 space-y-2 rounded-lg border px-2.5 py-2 text-xs">
      <div className="flex items-center gap-2">
        <MailSearch className="text-muted-foreground size-3.5 shrink-0" />
        <span className="text-foreground/90 font-medium">{label}</span>
        <Loader className="text-muted-foreground ml-auto size-3.5 shrink-0 animate-spin" />
      </div>
      <div
        className="bg-border/70 h-1 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        aria-label={label}
      >
        <div
          className="bg-primary h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}
